import { MutableRefObject, useCallback, useEffect } from "react";
import {
  closestCenter,
  CollisionDetection,
  DragEndEvent,
  rectIntersection,
} from "@dnd-kit/core";
import {
  GetAutomationJourneyPlanner,
  SaveAutomationJourneyPlanner,
} from "../automation/automationService";
import { addNotification } from "../../shell/notification/notifications";
import {
  getJourneyAlignmentSnappedPosition,
  getJourneyPlannerDropPosition,
  getJourneyPlannerScaledDelta,
} from "./journeyPlannerHelpers";
import { buildSavePlannerPayload } from "./journeyPlannerState";
import {
  ActiveDrag,
  DropData,
  Journey,
  JourneyRefs,
  Planner,
  PlannerAutomation,
  PlannerNavigation,
  SetPlanner,
} from "./journeyPlanner.types";

type GlobalDispatch = Parameters<typeof addNotification>[0];

async function loadJourneyPlanner({
  setLoading,
  setPlanner,
  setIsDirty,
}: {
  setLoading: (loading: boolean) => void;
  setPlanner: SetPlanner;
  setIsDirty: (dirty: boolean) => void;
}) {
  setLoading(true);

  const response = await GetAutomationJourneyPlanner(null);
  if (response?.status === 200 && response.data) {
    setPlanner(response.data);
    setIsDirty(false);
  }

  setLoading(false);
}

function showJourneyPlannerNotification(
  globalDispatch: GlobalDispatch,
  message: string,
  success: boolean
) {
  addNotification(globalDispatch, message, success);
}

async function saveJourneyPlanner({
  globalDispatch,
  journeys,
  setSaving,
  setPlanner,
  setIsDirty,
}: {
  globalDispatch: GlobalDispatch;
  journeys: Journey[];
  setSaving: (saving: boolean) => void;
  setPlanner: SetPlanner;
  setIsDirty: (dirty: boolean) => void;
}) {
  setSaving(true);

  const response = await SaveAutomationJourneyPlanner(
    globalDispatch,
    buildSavePlannerPayload(journeys)
  );

  if (response?.status === 200 && response.data) {
    setPlanner(response.data);
    setIsDirty(false);
  }

  setSaving(false);
}

const journeyPlannerCollisionDetection: CollisionDetection = (args) => {
  const activeType = args.active?.data?.current?.type;

  if (
    activeType === "sidebar-automation" ||
    activeType === "journey-item"
  ) {
    const dropSlots = args.droppableContainers.filter(
      (container) => container.data.current?.type === "journey-drop"
    );

    return rectIntersection({
      ...args,
      droppableContainers: dropSlots,
    });
  }

  return closestCenter(args);
};

function useJourneyPlannerNotification(globalDispatch: GlobalDispatch) {
  return useCallback(
    (message: string, success: boolean) => {
      showJourneyPlannerNotification(
        globalDispatch,
        message,
        success
      );
    },
    [globalDispatch]
  );
}

function useJourneyPlannerSave({
  globalDispatch,
  plannerRef,
  setIsDirty,
  setPlanner,
  setSaving,
}: {
  globalDispatch: GlobalDispatch;
  plannerRef: MutableRefObject<Planner>;
  setIsDirty: (dirty: boolean) => void;
  setPlanner: SetPlanner;
  setSaving: (saving: boolean) => void;
}) {
  return useCallback(async () => {
    await saveJourneyPlanner({
      globalDispatch,
      journeys: plannerRef.current?.Journeys || [],
      setIsDirty,
      setPlanner,
      setSaving,
    });
  }, [
    globalDispatch,
    plannerRef,
    setIsDirty,
    setPlanner,
    setSaving,
  ]);
}

function useJourneyPlannerNavigation({
  handleSavePlanner,
  isDirty,
  saving,
  setPlannerNavigation,
}: {
  handleSavePlanner: () => Promise<void>;
  isDirty: boolean;
  saving: boolean;
  setPlannerNavigation: (navigation: PlannerNavigation) => void;
}) {
  useEffect(() => {
    setPlannerNavigation({
      isDirty,
      onSave: handleSavePlanner,
      saving,
    });

    return () => {
      setPlannerNavigation({
        isDirty: false,
        onSave: null,
        saving: false,
      });
    };
  }, [handleSavePlanner, isDirty, saving, setPlannerNavigation]);
}

interface DragEndHandlers {
  planner: Planner;
  journeyRefs: JourneyRefs;
  replaceJourney: (journey: Journey) => void;
  addAutomationToJourney: (
    journeyId: number,
    automation: PlannerAutomation,
    insertIndex?: number | null
  ) => void;
  reorderJourneyItems: (
    journeyId: number,
    oldIndex: number,
    newIndex: number
  ) => void;
  setActiveDrag: (drag: ActiveDrag | null) => void;
  showNotification: (message: string, success: boolean) => void;
  zoomLevel: number;
}

function handleJourneyPlannerDragEnd({
  event,
  planner,
  journeyRefs,
  replaceJourney,
  addAutomationToJourney,
  reorderJourneyItems,
  setActiveDrag,
  showNotification,
  zoomLevel,
}: DragEndHandlers & { event: DragEndEvent }) {
  const { active, over, delta } = event;
  const dragData = active?.data?.current as ActiveDrag | undefined;

  setActiveDrag(null);

  if (!dragData) {
    return;
  }

  if (dragData.type === "journey-card") {
    const journey = planner.Journeys.find(
      (currentJourney) => currentJourney.Id === dragData.journeyId
    );

    if (!journey || (!delta.x && !delta.y)) {
      return;
    }

    const scaledDelta = getJourneyPlannerScaledDelta(
      delta,
      zoomLevel
    );
    const draggedPosition = {
      x: Math.max(0, Math.round((journey.X ?? 0) + scaledDelta.x)),
      y: Math.max(0, Math.round((journey.Y ?? 0) + scaledDelta.y)),
    };
    const nextPosition = getJourneyAlignmentSnappedPosition(
      planner.Journeys,
      dragData.journeyId as number,
      draggedPosition
    );
    const dropPosition = getJourneyPlannerDropPosition({
      draggedPosition,
      journey,
      journeyRefs,
      journeys: planner.Journeys,
      snappedPosition: nextPosition,
    });

    if (!dropPosition) {
      return;
    }

    replaceJourney({
      ...journey,
      X: dropPosition.x,
      Y: dropPosition.y,
    });

    return;
  }

  const overData = over?.data?.current as DropData | undefined;

  if (!overData || overData.type !== "journey-drop") {
    return;
  }

  if (dragData.type === "sidebar-automation") {
    const targetJourney = planner.Journeys.find(
      (journey) => journey.Id === overData.journeyId
    );
    if (
      targetJourney &&
      targetJourney.Items.some(
        (item) => item.AutomationId === dragData.automationId
      )
    ) {
      showNotification(
        "That automation is already in this journey.",
        false
      );
      return;
    }

    const draggedAutomation =
      dragData.automation ||
      planner.Automations.find(
        (automation) => automation.Id === dragData.automationId
      );

    if (!draggedAutomation) {
      return;
    }

    addAutomationToJourney(
      overData.journeyId,
      draggedAutomation,
      overData.insertIndex
    );
    return;
  }

  if (
    dragData.type === "journey-item" &&
    dragData.journeyId === overData.journeyId
  ) {
    const journey = planner.Journeys.find(
      (currentJourney) => currentJourney.Id === dragData.journeyId
    );

    if (!journey) {
      return;
    }

    const oldIndex = journey.Items.findIndex(
      (item) => item.Id === dragData.itemId
    );

    if (oldIndex < 0) {
      return;
    }

    const insertIndex = overData.insertIndex ?? oldIndex;
    const newIndex =
      insertIndex > oldIndex ? insertIndex - 1 : insertIndex;

    if (newIndex === oldIndex) {
      return;
    }

    reorderJourneyItems(dragData.journeyId, oldIndex, newIndex);
  }
}

function useJourneyPlannerDragEnd({
  addAutomationToJourney,
  journeyRefs,
  planner,
  replaceJourney,
  reorderJourneyItems,
  setActiveDrag,
  showNotification,
  zoomLevel,
}: DragEndHandlers) {
  return useCallback(
    (event: DragEndEvent) => {
      handleJourneyPlannerDragEnd({
        addAutomationToJourney,
        event,
        journeyRefs,
        planner,
        replaceJourney,
        reorderJourneyItems,
        setActiveDrag,
        showNotification,
        zoomLevel,
      });
    },
    [
      addAutomationToJourney,
      journeyRefs,
      planner,
      replaceJourney,
      reorderJourneyItems,
      setActiveDrag,
      showNotification,
      zoomLevel,
    ]
  );
}

export {
  handleJourneyPlannerDragEnd,
  journeyPlannerCollisionDetection,
  loadJourneyPlanner,
  saveJourneyPlanner,
  showJourneyPlannerNotification,
  useJourneyPlannerDragEnd,
  useJourneyPlannerNavigation,
  useJourneyPlannerNotification,
  useJourneyPlannerSave,
};
